<template>
  <div id="app">
    <el-container>
      <el-header>
        <div class="header-content">
          <h2>微信消息监控系统</h2>
          <div class="connection-status">
            <el-tag :type="connectionStatus === 'connected' ? 'success' : connectionStatus === 'connecting' ? 'warning' : 'danger'">
              {{ connectionStatusText }}
            </el-tag>
          </div>
        </div>
      </el-header>
      
      <el-main>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-card class="box-card">
              <div slot="header" class="clearfix">
                <span>所有消息</span>
              </div>
              <div class="message-list" ref="allMessagesContainer">
                <el-table :data="allMessages" height="500" style="width: 100%">
                  <el-table-column prop="timestamp" label="时间" width="180">
                    <template #default="scope">
                      {{ formatTimestamp(scope.row.data?.data?.timeStamp || scope.row.timestamp) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="sender" label="发送者">
                    <template #default="scope">
                      {{ getMessageSender(scope.row) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="content" label="内容">
                    <template #default="scope">
                      {{ scope.row.data?.data?.msg || '无内容' }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-card>
          </el-col>
          
          <el-col :span="12">
            <el-card class="box-card">
              <div slot="header" class="clearfix">
                <span>关键词过滤消息</span>
                <el-input
                  v-model="keywordFilter"
                  placeholder="输入关键词，多个关键词用逗号分隔"
                  style="width: 300px; float: right;"
                  @input="updateFilteredMessages"
                ></el-input>
              </div>
              <div class="message-list" ref="filteredMessagesContainer">
                <el-table :data="filteredMessages" height="500" style="width: 100%">
                  <el-table-column prop="timestamp" label="时间" width="180">
                    <template #default="scope">
                      {{ formatTimestamp(scope.row.data?.data?.timeStamp || scope.row.timestamp) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="sender" label="发送者">
                    <template #default="scope">
                      {{ getMessageSender(scope.row) }}
                    </template>
                  </el-table-column>
                  <el-table-column prop="content" label="内容">
                    <template #default="scope">
                      {{ scope.row.data?.data?.msg || '无内容' }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </el-main>
    </el-container>
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      websocket: null,
      connectionStatus: 'disconnected', // connected, connecting, disconnected
      allMessages: [],
      filteredMessages: [],
      keywordFilter: '',
      groupInfoCache: {}, // 群信息缓存
      userInfoCache: {} // 用户信息缓存
    }
  },
  computed: {
    connectionStatusText() {
      switch (this.connectionStatus) {
        case 'connected': return '已连接'
        case 'connecting': return '连接中'
        case 'disconnected': return '已断开'
        default: return '未知状态'
      }
    }
  },
  mounted() {
    this.connectWebSocket()
    this.loadMessagesFromStorage()
  },
  beforeDestroy() {
    if (this.websocket) {
      this.websocket.close()
    }
  },
  methods: {
    connectWebSocket() {
      this.connectionStatus = 'connecting'
      
      // 直接连接WebSocket服务器（不使用代理）
      const wsUrl = 'wss://wechat-demo.itxiao6.top/ws?wxid=wxid_jckqh33xpg6t22'
      
      try {
        this.websocket = new WebSocket(wsUrl)
        
        this.websocket.onopen = () => {
          console.log('WebSocket连接已建立')
          this.connectionStatus = 'connected'
        }
        
        this.websocket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleMessage(message)
          } catch (error) {
            console.error('解析消息失败:', error)
          }
        }
        
        this.websocket.onerror = (error) => {
          console.error('WebSocket错误:', error)
          this.connectionStatus = 'disconnected'
        }
        
        this.websocket.onclose = () => {
          console.log('WebSocket连接已关闭，尝试重新连接...')
          this.connectionStatus = 'disconnected'
          // 5秒后重连
          setTimeout(this.connectWebSocket, 5000)
        }
      } catch (error) {
        console.error('WebSocket连接失败:', error)
        this.connectionStatus = 'disconnected'
      }
    },
    
    handleMessage(message) {
      // 保存到本地存储
      this.saveMessage(message)
      
      // 添加到所有消息列表
      this.allMessages.unshift(message)
      
      // 限制消息数量
      if (this.allMessages.length > 1000) {
        this.allMessages.splice(1000)
      }
      
      // 检查是否匹配关键词
      if (this.matchesKeyword(message)) {
        this.filteredMessages.unshift(message)
        
        // 限制过滤消息数量
        if (this.filteredMessages.length > 200) {
          this.filteredMessages.splice(200)
        }
      }
      
      // 处理不同类型的消息
      const data = message.data?.data || {}
      if (data.fromType === 2 && data.fromWxid) {
        // 群消息，查询群聊信息
        this.queryAndCacheGroupInfo(data.fromWxid)
      } else if (data.fromType === 1 && data.fromWxid) {
        // 私聊消息，查询对象信息
        this.queryAndCacheUserInfo(data.fromWxid)
      }
    },
    
    saveMessage(message) {
      try {
        const messages = JSON.parse(localStorage.getItem('wechatMessages') || '[]')
        messages.unshift(message)
        
        // 只保留最近1000条消息
        if (messages.length > 1000) {
          messages.splice(1000)
        }
        
        localStorage.setItem('wechatMessages', JSON.stringify(messages))
      } catch (error) {
        console.error('保存消息失败:', error)
      }
    },
    
    loadMessagesFromStorage() {
      try {
        const messages = JSON.parse(localStorage.getItem('wechatMessages') || '[]')
        this.allMessages = messages
        
        // 加载过滤消息
        this.updateFilteredMessages()
        
        // 加载缓存的群信息和用户信息
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key.startsWith('groupInfo_')) {
            const groupWxid = key.substring(10)
            this.groupInfoCache[groupWxid] = JSON.parse(localStorage.getItem(key))
          } else if (key.startsWith('userInfo_')) {
            const userWxid = key.substring(9)
            this.userInfoCache[userWxid] = JSON.parse(localStorage.getItem(key))
          }
        }
      } catch (error) {
        console.error('加载历史消息失败:', error)
      }
    },
    
    async queryAndCacheGroupInfo(groupWxid) {
      // 检查是否已经缓存
      if (this.groupInfoCache[groupWxid]) {
        console.log(`群信息已缓存: ${groupWxid}`)
        return this.groupInfoCache[groupWxid]
      }
      
      try {
        // 检查localStorage中是否已有缓存
        const cachedGroupInfo = localStorage.getItem(`groupInfo_${groupWxid}`)
        if (cachedGroupInfo) {
          const parsedInfo = JSON.parse(cachedGroupInfo)
          this.groupInfoCache[groupWxid] = parsedInfo
          console.log(`群信息已从localStorage加载: ${groupWxid}`)
          return parsedInfo
        }
        
        // 发送HTTP请求查询群信息
        const requestData = {
          type: "queryGroup",
          data: {
            wxid: groupWxid,
            type: "1" // 从缓存中获取
          }
        }
        
        const response = await this.$http.post('/api/httpapi', requestData)
        
        if (response.data.code === 200 && response.data.result) {
          // 缓存群信息
          this.groupInfoCache[groupWxid] = response.data.result
          localStorage.setItem(`groupInfo_${groupWxid}`, JSON.stringify(response.data.result))
          console.log(`群信息查询成功并已缓存: ${groupWxid}`, response.data.result)
          return response.data.result
        } else {
          console.error(`查询群信息失败: ${response.data.msg}`)
        }
      } catch (error) {
        console.error('查询群信息时发生错误:', error)
      }
      return null
    },
    
    async queryAndCacheUserInfo(userWxid) {
      // 检查是否已经缓存
      if (this.userInfoCache[userWxid]) {
        console.log(`用户信息已缓存: ${userWxid}`)
        return this.userInfoCache[userWxid]
      }
      
      try {
        // 检查localStorage中是否已有缓存
        const cachedUserInfo = localStorage.getItem(`userInfo_${userWxid}`)
        if (cachedUserInfo) {
          const parsedInfo = JSON.parse(cachedUserInfo)
          this.userInfoCache[userWxid] = parsedInfo
          console.log(`用户信息已从localStorage加载: ${userWxid}`)
          return parsedInfo
        }
        
        // 发送HTTP请求查询用户信息
        const requestData = {
          type: "queryObj",
          data: {
            wxid: userWxid,
            type: "1" // 从缓存中获取
          }
        }
        
        const response = await this.$http.post('/api/httpapi', requestData)
        
        if (response.data.code === 200 && response.data.result) {
          // 缓存用户信息
          this.userInfoCache[userWxid] = response.data.result
          localStorage.setItem(`userInfo_${userWxid}`, JSON.stringify(response.data.result))
          console.log(`用户信息查询成功并已缓存: ${userWxid}`, response.data.result)
          return response.data.result
        } else {
          console.error(`查询用户信息失败: ${response.data.msg}`)
        }
      } catch (error) {
        console.error('查询用户信息时发生错误:', error)
      }
      return null
    },
    
    getMessageSender(message) {
      const data = message.data?.data || {}
      const fromWxid = data.fromWxid || '未知'
      const finalFromWxid = data.finalFromWxid || ''
      const fromType = data.fromType || 0
      
      if (fromType === 2) {
        // 群消息
        let groupName = ''
        if (this.groupInfoCache[fromWxid]) {
          groupName = this.groupInfoCache[fromWxid].nick ? `(${this.groupInfoCache[fromWxid].nick})` : ''
        }
        
        if (finalFromWxid) {
          // 显示发送者->群组
          let senderName = finalFromWxid
          if (this.userInfoCache[finalFromWxid]) {
            const userInfo = this.userInfoCache[finalFromWxid]
            senderName = userInfo.remark || userInfo.nick || finalFromWxid
          }
          return `${senderName} -> ${fromWxid}${groupName}`
        } else {
          return `${fromWxid}${groupName}`
        }
      } else if (fromType === 1) {
        // 个人消息
        let displayName = fromWxid
        if (this.userInfoCache[fromWxid]) {
          const userInfo = this.userInfoCache[fromWxid]
          displayName = userInfo.remark || userInfo.nick || fromWxid
          if (displayName !== fromWxid) {
            displayName = `${fromWxid}(${displayName})`
          }
        }
        return displayName
      } else {
        // 其他类型消息
        return `[类型${fromType}] ${fromWxid}`
      }
    },
    
    matchesKeyword(message) {
      if (!this.keywordFilter.trim()) return false
      
      const keywords = this.keywordFilter.split(',').map(k => k.trim()).filter(k => k)
      if (keywords.length === 0) return false
      
      const data = message.data?.data || {}
      const msgContent = data.msg || ''
      
      return keywords.some(keyword => 
        msgContent.includes(keyword)
      )
    },
    
    updateFilteredMessages() {
      if (!this.keywordFilter.trim()) {
        this.filteredMessages = []
        return
      }
      
      this.filteredMessages = this.allMessages.filter(message => 
        this.matchesKeyword(message)
      )
    },
    
    formatTimestamp(timestamp) {
      if (!timestamp) return '未知时间'
      const date = new Date(parseInt(timestamp))
      return date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.message-list {
  height: 500px;
  overflow-y: auto;
}

.el-card {
  height: 100%;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}
</style>